/**
 * The three states every phone list spends most of its life in: loading, failed,
 * and empty.
 *
 * Extracted because the bookings and invoices tabs had written all three twice, and
 * the failure case is the one that matters — it carries the server's own message plus
 * a retry, and a second copy is a second place for someone to swap that message for a
 * generic "something went wrong". The server's message is often the only thing that
 * tells a customer what to do next.
 *
 * Presentational only. Each screen decides WHICH state applies; centralising that
 * would mean this module knowing about slices it should not.
 */

import React from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';

export const ListLoading = () => (
    <div className="flex justify-center py-12">
        <Loader2 size={22} className="animate-spin text-emerald-600" />
    </div>
);

/**
 * A failed load, with the reason and a way to try again.
 *
 * `message` is the server's, passed through verbatim by the slices. A 400 from the
 * invoice filter says which date is wrong; replacing that with a generic string
 * leaves the customer with a broken screen and no next step.
 */
export const ListError = ({ message, onRetry }) => (
    <div className="m-3 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3">
        <AlertCircle size={16} className="mt-0.5 shrink-0 text-red-500" />
        <div>
            <p className="text-sm text-red-700">{message}</p>
            {onRetry && (
                <button
                    type="button"
                    onClick={onRetry}
                    className="mt-2 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white"
                >
                    Try again
                </button>
            )}
        </div>
    </div>
);

/**
 * An empty list.
 *
 * `detail` exists so a filtered empty state can name the filter. "No invoices" on a
 * screen quietly showing seven days reads as "you have never been billed", which is a
 * different and alarming claim.
 *
 * @param {{ icon: React.ComponentType, title: string, detail?: React.ReactNode,
 *           action?: React.ReactNode }} props
 */
export const ListEmpty = ({ icon: Icon, title, detail, action }) => (
    <div className="flex flex-col items-center justify-center px-8 py-16 text-center">
        {Icon && <Icon size={28} className="text-gray-300" />}
        <p className="mt-3 text-sm font-medium text-gray-900">{title}</p>
        {detail && <p className="mt-1 text-xs text-gray-500">{detail}</p>}
        {action && <div className="mt-4">{action}</div>}
    </div>
);
